import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import KpiCard from "./ui/KpiCard";
import SectionHeader from "./ui/SectionHeader";
import { calcularFechaFin } from "./subscriptions/utils";
import { obtenerFechaHoyISO, toLocalISOString } from "../../utils/horaLocal";
import VentaForm from "./VentaForm";
import VentasTable from "./VentasTable";
import { errorVenta, totalItems } from "./ventaUtils";

const FORM_INITIAL = {
  fechaVenta: obtenerFechaHoyISO(),
  leadId: "",
  nombreCliente: "",
  items: [{ productoId: "", productoNombre: "", cantidad: 1, precioUnitario: "" }],
  modoPago: "efectivo",
  montoEfectivo: "",
  montoTransferencia: "",
  montoCredito: "",
  vendedorId: "",
  vendedorNombre: "",
  opportunityId: "",
  subDates: {},
  fechaLimitePago: "",
};

export default function Ventas({ store, initialLeadId, initialOpportunityId, onInitialLeadConsumed }) {
  const [openForm, setOpenForm] = useState(false);
  const [filtroFecha, setFiltroFecha] = useState("");
  const [form, setForm] = useState({ ...FORM_INITIAL });
  useEffect(() => {
    if (!initialLeadId) return;
    const lead = store.leads.find(l => l.id === initialLeadId);
    if (lead) {
      setForm({ ...FORM_INITIAL, leadId: lead.id, nombreCliente: lead.nombre, opportunityId: initialOpportunityId || "" });
      setOpenForm(true);
    }
    onInitialLeadConsumed?.();
  }, [initialLeadId, initialOpportunityId, store.leads, onInitialLeadConsumed]);

  const productosRecurrentes = useMemo(
    () => form.items
      .map(it => it.productoId ? store.config.productos.find(p => p.id === it.productoId) : null)
      .filter(p => p?.esRecurrente),
    [form.items, store.config.productos]
  );

  const handleSubmit = () => {
    if (errorVenta(form)) return;
    const montoEf = parseFloat(form.montoEfectivo) || 0;
    const montoTr = parseFloat(form.montoTransferencia) || 0;
    const montoCr = parseFloat(form.montoCredito) || 0;
    const esCredito = montoCr > 0;
    const totalPago = montoEf + montoTr;

    const items = form.items
      .filter(it => it.productoNombre || it.productoId)
      .map(it => ({
        productoId: it.productoId || null,
        productoNombre: it.productoNombre || "",
        cantidad: parseInt(it.cantidad) || 1,
        precioUnitario: parseFloat(it.precioUnitario) || 0,
        subtotal: (parseFloat(it.precioUnitario) || 0) * (parseInt(it.cantidad) || 1),
      }));

    const totalProductos = totalItems(items);

    const hoy = obtenerFechaHoyISO();
    const fechaVenta = form.fechaVenta || hoy;
    const ventaData = {
      fecha: fechaVenta === hoy ? toLocalISOString() : `${fechaVenta}T12:00:00`,
      leadId: form.leadId,
      recompraDe: form.recompraDe || null,
      opportunityId: form.opportunityId || null,
      nombreCliente: form.nombreCliente,
      productoId: items[0]?.productoId || null,
      productoNombre: items.map(it => it.productoNombre).filter(Boolean).join(", ") || "",
      items,
      montoEfectivo: esCredito ? 0 : montoEf,
      montoTransferencia: esCredito ? 0 : montoTr,
      vendedorId: store.isOwner === false ? store.sellerId : form.vendedorId,
      vendedorNombre: store.isOwner === false ? store.config.vendedores.find(v => v.id === store.sellerId)?.nombre || '' : form.vendedorNombre,
      esCredito,
      fechaLimitePago: esCredito ? (form.fechaLimitePago || null) : null,
    };

    if (esCredito) {
      ventaData.montoEfectivo = 0;
      ventaData.montoTransferencia = 0;
      ventaData.total = totalProductos;
    }

    // Suscripción: usar primer producto recurrente
    if (productosRecurrentes.length > 0) {
      const prod = productosRecurrentes[0];
      const sub = form.subDates?.[prod.id] || {};
      const startDate = sub.startDate || obtenerFechaHoyISO();
      const dias = parseInt(sub.validityDays) || prod.diasVigencia || 30;
      ventaData.startDate = startDate;
      ventaData.endDate = calcularFechaFin(startDate, dias);
      ventaData.validityDays = dias;
      ventaData.reminderDays = parseInt(sub.reminderDays) || prod.diasRecordatorio || 7;
      if (form.renovadaDesde) ventaData.renovadaDesde = form.renovadaDesde;
    }

    const ventaId = store.registrarVenta(ventaData);

    // If credit sale with initial payment (anticipo), register it as first pagoCredito
    if (esCredito && totalPago > 0) {
      store.registrarPagoCredito({
        ventaId,
        clienteKey: form.leadId || form.nombreCliente,
        monto: totalPago,
        metodo: montoEf > 0 && montoTr > 0 ? "mixto" : montoEf > 0 ? "efectivo" : "transferencia",
        nota: "Anticipo al momento de la venta",
      });
    }

    setForm({ ...FORM_INITIAL });
    setOpenForm(false);
  };

  const handleRenovar = (v) => {
    const prod = store.config.productos.find(p => p.id === v.productoId);
    const items = v.items?.length > 0
      ? v.items.map(it => ({ productoId: it.productoId || "", productoNombre: it.productoNombre, cantidad: it.cantidad || 1, precioUnitario: String(it.precioUnitario ?? "") }))
      : [{ productoId: v.productoId || "", productoNombre: v.productoNombre || "", cantidad: 1, precioUnitario: String(v.total || "") }];

    const subDates = {};
    if (prod?.esRecurrente) {
      subDates[prod.id] = {
        startDate: obtenerFechaHoyISO(),
        validityDays: String(v.validityDays || prod.diasVigencia || 30),
        reminderDays: String(v.reminderDays || prod.diasRecordatorio || 7),
      };
    }

    setForm({
      ...FORM_INITIAL,
      leadId: v.leadId || "",
      nombreCliente: v.nombreCliente,
      items,
      montoEfectivo: String(totalItems(items)),
      montoTransferencia: "",
      vendedorId: v.vendedorId || "",
      vendedorNombre: v.vendedorNombre || "",
      subDates,
      renovadaDesde: v.id,
      recompraDe: v.id,
    });
    setOpenForm(true);
  };

  const ventasFiltradas = store.ventasActivas
    .filter((v) => !filtroFecha || v.fecha.startsWith(filtroFecha))
    .sort((a, b) => b.fecha.localeCompare(a.fecha));

  return (
    <Box>
      <SectionHeader
        title="Ventas"
        description="Registra cada venta para medir ingresos, recompras y cobranzas."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ ...FORM_INITIAL }); setOpenForm(true); }}>
            Nueva venta
          </Button>
        }
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <KpiCard tone="revenue" icon={<AttachMoneyIcon />} label="Ingresos hoy" value={`${store.config.moneda} ${store.totalVentasDelDia.toLocaleString()}`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="neutral" icon={<ReceiptLongOutlinedIcon />} label="Ventas hoy" value={store.ventasDelDia.length} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="revenue" icon={<AttachMoneyIcon />} label="Ingresos del mes" value={`${store.config.moneda} ${store.totalVentasDelMes.toLocaleString()}`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="neutral" icon={<ReceiptLongOutlinedIcon />} label="Ventas del mes" value={store.ventasDelMes.length} />
        </Grid>
      </Grid>

      <VentaForm
        open={openForm}
        store={store}
        form={form}
        setForm={setForm}
        productosRecurrentes={productosRecurrentes}
        onSubmit={handleSubmit}
        onCancel={() => setOpenForm(false)}
      />

      {/* Filtro */}
      <Stack direction="row" spacing={1} mb={2}>
        <TextField
          label="Filtrar por fecha"
          value={filtroFecha}
          onChange={(e) => setFiltroFecha(e.target.value)}
          type="date"
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        {filtroFecha && (
          <Button size="small" onClick={() => setFiltroFecha("")}>Limpiar</Button>
        )}
      </Stack>

      <VentasTable
        store={store}
        ventasFiltradas={ventasFiltradas}
        onRenovar={handleRenovar}
      />

      {ventasFiltradas.length >= 50 && (
        <Box mt={1} textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Mostrando las últimas 50 ventas
          </Typography>
        </Box>
      )}
    </Box>
  );
}
